import { type ReactNode, type CSSProperties, useState, useEffect } from 'react'

// ─── Color tokens ─────────────────────────────────────────────────────────────
export const C = {
  deepBlack: '#080711',
  darkPurple: '#140D26',
  primaryPurple: '#6D28D9',
  neonPurple: '#8B5CFF',
  magenta: '#F044C7',
  cyan: '#35D8FF',
  secText: '#AAA5BF',
  white: '#FFFFFF',
}

// ─── Responsive breakpoint hook ──────────────────────────────────────────────
export function useBreakpoint() {
  const [w, setW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1440))
  useEffect(() => {
    const fn = () => setW(window.innerWidth)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])
  return {
    isMobile: w < 768,
    isTablet: w >= 768 && w < 1200,
    isDesktop: w >= 1200,
    isXL: w >= 1440,
    width: w,
  }
}

// ─── GlowButton ──────────────────────────────────────────────────────────────
interface GlowBtnProps {
  children: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  fullWidth?: boolean
  style?: CSSProperties
  disabled?: boolean
  className?: string
}

export function GlowBtn({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  fullWidth,
  style,
  disabled,
  className,
}: GlowBtnProps) {
  const pad = { sm: '10px 22px', md: '14px 32px', lg: '18px 44px', xl: '22px 64px' }[size]
  const fs = { sm: '13px', md: '15px', lg: '17px', xl: '20px' }[size]

  const baseStyles: Record<string, CSSProperties> = {
    primary: {
      background: `linear-gradient(135deg, ${C.primaryPurple} 0%, ${C.neonPurple} 100%)`,
      boxShadow: `0 0 22px rgba(139,92,255,0.55), 0 0 55px rgba(139,92,255,0.25), inset 0 1px 0 rgba(255,255,255,0.15)`,
      border: '1px solid rgba(139,92,255,0.6)',
    },
    secondary: {
      background: 'rgba(20,13,38,0.6)',
      backdropFilter: 'blur(16px)',
      border: `1.5px solid rgba(139,92,255,0.45)`,
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07)',
    },
    ghost: {
      background: 'transparent',
      border: `1px solid rgba(170,165,191,0.3)`,
    },
    danger: {
      background: 'rgba(240,68,199,0.15)',
      border: `1.5px solid rgba(240,68,199,0.5)`,
      boxShadow: `0 0 16px rgba(240,68,199,0.3)`,
    },
  }

  const cls = `${variant === 'primary' ? 'btn-primary' : variant === 'secondary' ? 'btn-secondary' : 'btn-ghost'} ${className ?? ''}`

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cls}
      style={{
        ...baseStyles[variant],
        padding: pad,
        fontSize: fs,
        fontFamily: "'Exo 2', sans-serif",
        fontWeight: 700,
        letterSpacing: '0.08em',
        color: variant === 'ghost' ? C.secText : C.white,
        borderRadius: '12px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: fullWidth ? '100%' : undefined,
        textTransform: 'uppercase',
        opacity: disabled ? 0.45 : 1,
        minHeight: '48px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      {children}
    </button>
  )
}

// ─── GlassPanel ──────────────────────────────────────────────────────────────
interface GlassPanelProps {
  children: ReactNode
  style?: CSSProperties
  glow?: string
  className?: string
  onClick?: () => void
}

export function GlassPanel({ children, style, glow, className, onClick }: GlassPanelProps) {
  return (
    <div
      className={className}
      onClick={onClick}
      style={{
        background: 'rgba(20,13,38,0.65)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `1px solid rgba(139,92,255,0.22)`,
        borderRadius: '16px',
        boxShadow: glow
          ? `0 0 30px ${glow}22, inset 0 1px 0 rgba(255,255,255,0.07)`
          : 'inset 0 1px 0 rgba(255,255,255,0.07)',
        cursor: onClick ? 'pointer' : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

// ─── PlayerBadge ─────────────────────────────────────────────────────────────
export function PlayerBadge({
  id,
  name,
  size = 'md',
}: {
  id: string
  name?: string
  size?: 'sm' | 'md' | 'lg'
}) {
  const fs = { sm: '10px', md: '13px', lg: '16px' }[size]
  const pad = { sm: '4px 10px', md: '6px 14px', lg: '8px 20px' }[size]
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: 'rgba(109,40,217,0.25)',
        border: '1px solid rgba(139,92,255,0.5)',
        borderRadius: '999px',
        padding: pad,
      }}
    >
      <span
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: fs,
          fontWeight: 700,
          color: C.neonPurple,
        }}
      >
        PLAYER {id}
      </span>
      {name && <span style={{ fontSize: fs, fontWeight: 600, color: C.white }}>{name}</span>}
    </div>
  )
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────
type BadgeVariant = 'success' | 'processing' | 'warning' | 'pending'

export function StatusBadge({
  children,
  variant = 'processing',
}: {
  children: ReactNode
  variant?: BadgeVariant
}) {
  const colors: Record<BadgeVariant, { bg: string; border: string; dot: string }> = {
    success: { bg: 'rgba(0,230,118,0.15)', border: 'rgba(0,230,118,0.4)', dot: '#00E676' },
    processing: { bg: 'rgba(139,92,255,0.15)', border: 'rgba(139,92,255,0.4)', dot: C.neonPurple },
    warning: { bg: 'rgba(255,215,0,0.15)', border: 'rgba(255,215,0,0.4)', dot: '#FFD700' },
    pending: { bg: 'rgba(170,165,191,0.12)', border: 'rgba(170,165,191,0.3)', dot: C.secText },
  }
  const c = colors[variant]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: '999px',
        padding: '5px 12px',
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.06em',
        color: c.dot,
      }}
    >
      <span
        style={{
          width: '7px',
          height: '7px',
          borderRadius: '50%',
          background: c.dot,
          boxShadow: `0 0 6px ${c.dot}`,
          animation: variant === 'processing' ? 'glow-pulse 1.4s ease-in-out infinite' : undefined,
          flexShrink: 0,
        }}
      />
      {children}
    </span>
  )
}

// ─── NeonText ─────────────────────────────────────────────────────────────────
interface NeonTextProps {
  children: ReactNode
  color?: string
  size?: number | string
  weight?: number
  style?: CSSProperties
  as?: 'h1' | 'h2' | 'h3' | 'p' | 'span' | 'div'
}

export function NeonText({
  children,
  color = C.neonPurple,
  size = 48,
  weight = 900,
  style,
  as: Tag = 'div',
}: NeonTextProps) {
  return (
    <Tag
      style={{
        fontFamily: "'Exo 2', sans-serif",
        fontSize: typeof size === 'number' ? `${size}px` : size,
        fontWeight: weight,
        color,
        textShadow: `0 0 10px ${color}99, 0 0 24px ${color}66, 0 0 48px ${color}33`,
        letterSpacing: '0.04em',
        lineHeight: 1.1,
        ...style,
      }}
    >
      {children}
    </Tag>
  )
}

// Dekoratif alanlar (yıldız/parçacık) için **deterministik** gürültü: `Math.random()` render
// sırasında saf değildir — her render'da yıldızlar yerinden oynardı ve React kuralını kırardı.
function noise(index: number, salt: number): number {
  const x = Math.sin((index + 1) * 12.9898 + salt * 78.233) * 43758.5453
  return x - Math.floor(x)
}

// ─── Stars background ─────────────────────────────────────────────────────────
export function Stars({ count = 60 }: { count?: number }) {
  const stars = Array.from({ length: count }, (_, i) => ({
    x: noise(i, 1) * 100,
    y: noise(i, 2) * 100,
    size: noise(i, 3) * 2.5 + 0.5,
    delay: noise(i, 4) * 4,
    dur: noise(i, 5) * 3 + 2,
    color: noise(i, 6) > 0.7,
  }))
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {stars.map((s, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: s.size,
            height: s.size,
            borderRadius: '50%',
            background: s.color ? C.cyan : C.neonPurple,
            boxShadow: `0 0 ${s.size * 3}px currentColor`,
            animation: `star-twinkle ${s.dur}s ${s.delay}s ease-in-out infinite`,
          }}
        />
      ))}
    </div>
  )
}

// ─── Particles ────────────────────────────────────────────────────────────────
export function Particles({
  count = 20,
  color = C.neonPurple,
}: {
  count?: number
  color?: string
}) {
  const ps = Array.from({ length: count }, (_, i) => ({
    x: noise(i, 7) * 100,
    y: noise(i, 8) * 100,
    tx: (noise(i, 9) - 0.5) * 80,
    size: noise(i, 10) * 4 + 2,
    delay: noise(i, 11) * 5,
    dur: noise(i, 12) * 4 + 3,
  }))
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {ps.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: color,
            boxShadow: `0 0 ${p.size * 2}px ${color}`,
            ['--tx' as string]: `${p.tx}px`,
            animation: `particle-up ${p.dur}s ${p.delay}s ease-out infinite`,
          }}
        />
      ))}
    </div>
  )
}

// ─── SidePanel ────────────────────────────────────────────────────────────────
// Consistent styled panel for left/right columns
export function SidePanel({
  children,
  style,
  border,
}: {
  children: ReactNode
  style?: CSSProperties
  border?: 'left' | 'right'
}) {
  return (
    <div
      style={{
        background: 'rgba(8,7,17,0.7)',
        backdropFilter: 'blur(12px)',
        borderLeft: border === 'left' ? '1px solid rgba(139,92,255,0.15)' : undefined,
        borderRight: border === 'right' ? '1px solid rgba(139,92,255,0.15)' : undefined,
        display: 'flex',
        flexDirection: 'column',
        padding: '28px 24px',
        gap: '16px',
        overflowY: 'auto',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

// ─── ProcessingTimeline ──────────────────────────────────────────────────────
type StageState = 'done' | 'active' | 'pending'
interface TimelineStage {
  label: string
  state: StageState
}

export function ProcessingTimeline({ stages }: { stages: TimelineStage[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {stages.map((s, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div
            style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                s.state === 'done'
                  ? '#00E67622'
                  : s.state === 'active'
                    ? `${C.neonPurple}22`
                    : 'rgba(170,165,191,0.08)',
              border:
                s.state === 'done'
                  ? '1.5px solid #00E676'
                  : s.state === 'active'
                    ? `1.5px solid ${C.neonPurple}`
                    : '1.5px solid rgba(170,165,191,0.25)',
              boxShadow:
                s.state === 'done'
                  ? '0 0 10px #00E67644'
                  : s.state === 'active'
                    ? `0 0 12px ${C.neonPurple}66`
                    : 'none',
              fontSize: '13px',
            }}
          >
            {s.state === 'done' ? (
              '✓'
            ) : s.state === 'active' ? (
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: C.neonPurple,
                  animation: 'glow-pulse 0.8s ease-in-out infinite',
                  display: 'block',
                }}
              />
            ) : (
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: 'rgba(170,165,191,0.3)',
                  display: 'block',
                }}
              />
            )}
          </div>
          <span
            style={{
              fontSize: '15px',
              fontWeight: s.state === 'active' ? 700 : 500,
              color: s.state === 'done' ? '#00E676' : s.state === 'active' ? C.white : C.secText,
            }}
          >
            {s.label}
          </span>
        </div>
      ))}
    </div>
  )
}
